import { useCallback, useEffect, useState } from "react";
import { AppState, Platform } from "react-native";

import { ApiError, fetchAuthedBlob } from "@/api/client";
import { captureMessage } from "@/lib/telemetry";

export type AuthedImageSource = {
  uri: string;
  headers: Record<string, string>;
};

export type AuthedImageResult = {
  source: { uri: string } | AuthedImageSource | null;
  loading: boolean;
  /** The server said this photo is not there: deleted, hidden, or not yours. */
  gone: boolean;
  /** It did not load, and the reason says nothing about the photo. */
  unreachable: boolean;
  retry: () => void;
};

/**
 * How long to wait before each automatic retry of a transient failure.
 *
 * Three tries over about fifteen seconds, then it stops and waits for the
 * reader — a photo that has failed four times is not going to be fixed by a
 * fifth request a second later, and a bubble retrying forever in the background
 * is a bubble nobody asked to keep spending battery on.
 */
const RETRY_DELAYS_MS = [1_000, 4_000, 10_000];

/** Statuses that mean the photo itself is not available, rather than us. */
function isGone(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 404 || error.status === 403);
}

/**
 * Resolve an authorised image source into something the current platform can
 * actually render.
 *
 * Photo bytes are served by an authenticated route — there is no public URL for
 * a photo, by design. Native `Image` sends the header for us, so the source
 * passes straight through. The browser's `<img src>` cannot carry a header at
 * all, so on web we fetch the bytes ourselves and hand back a blob URL, revoked
 * on unmount so a long thread does not leak one object URL per photo.
 *
 * A failure here is usually NOT proof the photo is gone, and the caller must be
 * able to tell the difference. Three rounds of "this photo is no longer
 * available" on photos that were perfectly fine came from collapsing every
 * failure into that one claim:
 *
 *  1. The fetch used a bare `fetch` with the token snapshotted at render, so an
 *     expired access token 401'd — now it refreshes like every other call.
 *  2. The failure flag latched, so a later success still reported failure.
 *  3. And a 401, a dropped connection and a sleeping laptop still all rendered
 *     as a deleted photo, with nothing to do about it but reload the page.
 *
 * So the reason is carried out of here. Only 404 and 403 — the server saying
 * the photo is not there or not yours — mean `gone`. Everything else is
 * `unreachable`: retried a few times on its own, retryable by hand after that,
 * and never described to anyone as a photo that no longer exists.
 */
export function useAuthedImage(source: AuthedImageSource | null): AuthedImageResult {
  const uri = source?.uri ?? null;
  // Every result carries the URI it belongs to, so switching photos discards
  // the previous one at render time — no state reset inside the effect.
  const [fetched, setFetched] = useState<{ uri: string; blobUri: string } | null>(
    null,
  );
  const [failure, setFailure] = useState<{
    uri: string;
    gone: boolean;
    tries: number;
  } | null>(null);
  // Bumped to force a retry of a URI that previously failed.
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (Platform.OS !== "web" || !uri) return;

    let objectUrl: string | null = null;
    let cancelled = false;

    void (async () => {
      try {
        const blob = await fetchAuthedBlob(uri);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setFetched({ uri, blobUri: objectUrl });
        // Clear a previous failure for this URI. Without this a successful
        // retry still reported failure, so the tombstone stayed up even once
        // the bytes were in hand.
        setFailure((previous) => (previous?.uri === uri ? null : previous));
      } catch (error) {
        if (cancelled) return;
        const gone = isGone(error);
        setFailure((previous) => ({
          uri,
          gone,
          // Counted per URI so a photo that has never failed starts at one
          // rather than inheriting the count of the photo before it.
          tries: previous?.uri === uri ? previous.tries + 1 : 1,
        }));

        // Reported because this is the failure that keeps coming back and there
        // has been nothing to diagnose it with — the status is the whole
        // question, and it never left the browser. No URI: it identifies a
        // photo, and a photo identifies two people.
        if (!gone) {
          captureMessage("photo_thumb_unreachable", {
            status: error instanceof ApiError ? error.status : 0,
          });
        }
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [uri, attempt]);

  /**
   * Retry a transient failure on a short backoff.
   *
   * This is the case that produced the report: a conversation left open, a
   * token that expired while nobody was looking, and photos that stayed
   * "unavailable" until a manual reload. Waiting for the tab to be re-focused
   * was not enough — a window left sitting in the background of the same tab
   * never fires that event at all.
   */
  useEffect(() => {
    if (Platform.OS !== "web" || !uri) return;
    if (!failure || failure.uri !== uri || failure.gone) return;
    if (failure.tries > RETRY_DELAYS_MS.length) return;

    const delay = RETRY_DELAYS_MS[failure.tries - 1] ?? RETRY_DELAYS_MS.at(-1)!;
    const timer = setTimeout(retry, delay);
    return () => clearTimeout(timer);
  }, [uri, failure, retry]);

  /**
   * And once more when the tab comes back, however long it was away — the
   * backoff has usually given up by then. Only failures are retried, so a
   * conversation whose photos loaded fine costs nothing on every focus.
   */
  useEffect(() => {
    if (Platform.OS !== "web" || !uri) return;
    if (!failure || failure.uri !== uri || failure.gone) return;

    const subscription = AppState.addEventListener("change", (state) => {
      if (state === "active") retry();
    });
    return () => subscription.remove();
  }, [uri, failure, retry]);

  if (Platform.OS !== "web") {
    return { source, loading: false, gone: false, unreachable: false, retry };
  }
  if (!uri) {
    return { source: null, loading: false, gone: false, unreachable: false, retry };
  }

  const blobUri = fetched?.uri === uri ? fetched.blobUri : null;
  // Bytes win over a stale failure flag: if we have the image, show it.
  const failed = !blobUri && failure?.uri === uri;

  return {
    source: blobUri ? { uri: blobUri } : null,
    loading: !blobUri && !failed,
    gone: failed && failure.gone,
    unreachable: failed && !failure.gone,
    retry,
  };
}
